/**
 * 出局上限表 — 与奖励分发配置同权限；点击基数 / 倍数调整。
 */
import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  InputNumber,
  Modal,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';

interface ExitCapRow {
  id: string;
  kind: string;
  path_zh: string;
  star_package: string;
  coin_label: string;
  principal_usd: string;
  exit_multiplier: string;
  cap_usd: string;
  default_principal_usd: string;
  default_exit_multiplier: string;
  principal_source: 'uo_config' | 'code_default';
  multiplier_source: 'uo_config' | 'code_default';
}

type EditField = 'principal' | 'multiplier';

export function ExitCapTablePage() {
  const qc = useQueryClient();
  const hasNode = useAuthStore((s) => s.hasNode);
  const canEdit = hasNode('system.config.edit');
  const [edit, setEdit] = useState<{ row: ExitCapRow; field: EditField } | null>(null);
  const [form] = Form.useForm<{ value: number }>();
  const draft = Form.useWatch('value', form);

  const { data, isFetching } = useQuery({
    queryKey: ['admin', 'exit-cap-table'],
    queryFn: () => api.get<{ items: ExitCapRow[] }>('/api/admin/exit-cap-table'),
  });

  const items = data?.items ?? [];
  const pathRows = useMemo(
    () => items.filter((r) => r.kind !== 'main'),
    [items],
  );
  const mainRows = useMemo(
    () => items.filter((r) => r.kind === 'main'),
    [items],
  );

  function openEdit(row: ExitCapRow, field: EditField) {
    setEdit({ row, field });
    form.setFieldsValue({
      value: Number(field === 'principal' ? row.principal_usd : row.exit_multiplier),
    });
  }

  async function save(values: { value: number }) {
    if (!edit) return;
    const body =
      edit.field === 'principal'
        ? { principal_usd: String(values.value) }
        : { exit_multiplier: String(values.value) };
    try {
      await api.put(`/api/admin/exit-cap-table/${encodeURIComponent(edit.row.id)}`, body);
      message.success('已保存，只影响之后新快照');
      setEdit(null);
      qc.invalidateQueries({ queryKey: ['admin', 'exit-cap-table'] });
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '保存失败');
    }
  }

  const capPreview = (() => {
    if (!edit || draft == null || !Number.isFinite(Number(draft))) return null;
    const p = edit.field === 'principal' ? Number(draft) : Number(edit.row.principal_usd);
    const m = edit.field === 'multiplier' ? Number(draft) : Number(edit.row.exit_multiplier);
    return p * m;
  })();

  function columns() {
    return [
      { title: '路径', dataIndex: 'path_zh', width: 180 },
      { title: '配套', dataIndex: 'star_package', width: 80 },
      { title: '币种', dataIndex: 'coin_label', width: 140 },
      {
        title: '基数',
        dataIndex: 'principal_usd',
        width: 140,
        align: 'right' as const,
        render: (_: string, row: ExitCapRow) => (
          <span>
            {canEdit ? (
              <Button type="link" size="small" onClick={() => openEdit(row, 'principal')}>
                {row.principal_usd}
              </Button>
            ) : (
              row.principal_usd
            )}
            {row.principal_source === 'uo_config' ? (
              <Tag color="green" style={{ marginLeft: 4 }}>改</Tag>
            ) : null}
          </span>
        ),
      },
      {
        title: '倍数',
        dataIndex: 'exit_multiplier',
        width: 140,
        align: 'right' as const,
        render: (_: string, row: ExitCapRow) => (
          <span>
            {canEdit ? (
              <Button type="link" size="small" onClick={() => openEdit(row, 'multiplier')}>
                {row.exit_multiplier}
              </Button>
            ) : (
              row.exit_multiplier
            )}
            {row.multiplier_source === 'uo_config' ? (
              <Tag color="green" style={{ marginLeft: 4 }}>改</Tag>
            ) : null}
          </span>
        ),
      },
      {
        title: '出局上限',
        dataIndex: 'cap_usd',
        width: 120,
        align: 'right' as const,
      },
      ...(canEdit
        ? [
            {
              title: '',
              key: 'reset',
              width: 88,
              render: (_: unknown, row: ExitCapRow) =>
                row.principal_source === 'uo_config' || row.multiplier_source === 'uo_config' ? (
                  <Button
                    type="link"
                    size="small"
                    onClick={() => {
                      void (async () => {
                        try {
                          await api.put(`/api/admin/exit-cap-table/${encodeURIComponent(row.id)}`, {
                            reset: 'row',
                          });
                          message.success('已恢复代码默认');
                          qc.invalidateQueries({ queryKey: ['admin', 'exit-cap-table'] });
                        } catch (e) {
                          message.error(e instanceof ApiError ? e.message : '恢复失败');
                        }
                      })();
                    }}
                  >
                    恢复默认
                  </Button>
                ) : null,
            },
          ]
        : []),
    ];
  }

  return (
    <div>
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        出局上限表
      </Typography.Title>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="出局上限 = 基数 × 倍数。点击基数或倍数修改；只影响之后新注册 / 开子 / 原点升级快照，已快照用户不回溯。"
      />

      <Table<ExitCapRow>
        size="small"
        bordered
        rowKey="id"
        loading={isFetching}
        dataSource={pathRows}
        pagination={false}
        columns={columns()}
        scroll={{ x: 900 }}
      />

      <Typography.Title level={5} style={{ marginTop: 24 }}>
        主号开通（扣 SP2+TP，无注册币种）
      </Typography.Title>
      <Table<ExitCapRow>
        size="small"
        bordered
        rowKey="id"
        loading={isFetching}
        dataSource={mainRows}
        pagination={false}
        columns={columns()}
        scroll={{ x: 900 }}
      />

      <Modal
        open={edit !== null}
        title={
          edit
            ? `调整${edit.field === 'principal' ? '基数' : '倍数'} · ${edit.row.path_zh} ${edit.row.star_package} ${edit.row.coin_label}`
            : ''
        }
        onCancel={() => setEdit(null)}
        onOk={() => form.submit()}
        destroyOnClose
        okText="保存"
      >
        <Form form={form} layout="vertical" onFinish={save}>
          <Form.Item
            name="value"
            label={edit?.field === 'principal' ? '基数 (USD)' : '倍数'}
            rules={[{ required: true, message: '请输入正数' }]}
            extra={
              edit
                ? `代码默认 ${edit.field === 'principal' ? edit.row.default_principal_usd : edit.row.default_exit_multiplier}`
                : undefined
            }
          >
            <InputNumber min={0.0001} step={edit?.field === 'multiplier' ? 0.1 : 1} style={{ width: '100%' }} />
          </Form.Item>
          {capPreview != null && Number.isFinite(capPreview) ? (
            <Typography.Paragraph type="secondary">
              预览出局上限：<strong>{capPreview}</strong>
            </Typography.Paragraph>
          ) : null}
        </Form>
      </Modal>
    </div>
  );
}

export default ExitCapTablePage;
